import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService, PrimeNGConfig, FilterService, MenuItem } from 'primeng/api';
import { Table } from 'primeng/table';
import { saveAs } from 'file-saver';
import { AccessLevelEnum, ApplianceRole, AuthenticationService, CadenceRole } from 'src/app/services/authentication/authentication.service';
import { ProspectsService } from 'src/app/shared/services/prospects.service';
import { Prospect } from 'src/app/domain/prospect.model';
import { SpinnerServiceService } from 'src/app/shared/services/spinner-service.service';
import jsPDF from 'jspdf';
import { DatePipe } from '@angular/common';
import autoTable from 'jspdf-autotable';
import { SalesService, Users } from 'src/app/shared/services/sales.service';
import { UserConfigService, LoggingUser } from 'src/app/shared/services/user-config.service';
import { CustomerProspectDTO } from 'src/app/domain/customer-prospect.dto';
import { ProspectCustomerDetailsDTO } from 'src/app/domain/prospect-customer-details.dto';


interface DropdownOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-prospects',
  templateUrl: './prospects.component.html',
  styleUrls: ['./prospects.component.scss'],
  providers: [MessageService]
})
export class ProspectsComponent implements OnInit {
   @ViewChild('dt1') table: Table;
  prospectsData: Prospect[] = [];
  loading: boolean = true;
  selectedProspect: Prospect | null = null;
  displayUpdateModal: boolean = false;
  
  // Export menu items for split button
  exportMenuItems: MenuItem[] = [];

  // User tag-based access control
  userId: number;
  userTags: any = {};
  tagCodes = {
    allProspects: 'all_pros_btn',
    createOppNeeded: 'create_opp_ndd_btn', 
    linkOppNeeded: 'link_opp_ndd_btn',
    activities: 'activities_btn'
  };

  // For status update modal
  currentState: string = '';
  nextStateOptions: string[] = [];
  formattedNextStateOptions: DropdownOption[] = [];
  selectedNextState: string = '';
  commentText: string = '';
  attachment: File | null = null;

  // filter Customer
  optionsCustomer = [];
  selectedCustomers: any [] = [];
  // filter Created By
  optionsCreator = [];
  selectedCreators: any [] = [];
  // filter Brand
  optionsBrand = [];
  selectedBrands: String [] = [];
  // filter Sector
  optionsSector = [];
  selectedSectors: any [] = [];
  // filter sales
  optionsSales = [];
  selectedSales: any [] = [];

  // filter opportunity
  optionsOpportunity = [];
  selectedOpportunities: any [] = [];
  // dialog
  displayBrands = false;
  selectedProspectBrands = [];
  
  // Add Activity Dialog properties
  displayAddActivity = false;
  technicalLeadsOptions: Users[] = [];
  currentUser: LoggingUser;
  
  // New activity properties
  newActivityTitle: string = '';
  newActivityDescription: string = '';
  newActivityAttendance: number[] = [];
  newActivityAttachment: File | null = null;
  newActivityDate: Date | null = null;
  newAttachmentBase64: { attachmentName: string; content: string; contentType: string } | null = null;
  selectedProspectForActivity: Prospect | null = null;



  // workflow states mapping
  workflowStates: {[key: string]: string[]} = {
    'New': ['Contacted'],
    'Contacted': ['Engaged'],
    'Engaged': ['Qualified', 'De-Qualified'],
    'Qualified': ['Proposal Submission', 'Closed as Lost'],
    // 'InProgress': ['Proposal Submission', 'Closed as Lost'],
    'Proposal Submission': ['Proposal sent', 'Closed as Lost'],
    'Proposal sent': ['Negotiation', 'Closed as Lost'],
    'Negotiation': ['Closed as Won', 'Closed as Lost', 'Re-engage Later'],
    'De-Qualified': [],
    'Closed as Won': [],
    'Closed as Lost': [],
    'Re-engage Later': ['Contacted']
  };

  statusOptions = [
    { label: 'New', value: 'New' },
    { label: 'Contacted', value: 'Contacted' },
    { label: 'Engaged', value: 'Engaged' },
    { label: 'Qualified', value: 'Qualified' },
    // { label: 'InProgress', value: 'InProgress' },
    { label: 'Proposal Submission', value: 'Proposal Submission' },
    { label: 'Proposal sent', value: 'Proposal sent' },
    { label: 'Negotiation', value: 'Negotiation' },
    { label: 'De-Qualified', value: 'De-Qualified' },
    { label: 'Closed as Won', value: 'Closed as Won' },
    { label: 'Closed as Lost', value: 'Closed as Lost' },
    { label: 'Re-engage Later', value: 'Re-engage Later' },
  ];

  // roles
  applianceRole: ApplianceRole;
  cadenceRole: CadenceRole;
  public accessLevelEnum = AccessLevelEnum;



  // Mock data


  constructor(
    private prospectsService: ProspectsService,
    public authenticationService: AuthenticationService,
    private messageService: MessageService,
    private router: Router,
    private confirmationService: ConfirmationService,
    private datePipe: DatePipe,
    private primengConfig: PrimeNGConfig,
    private filterService: FilterService,
    private salesService: SalesService,
    private userConfigService: UserConfigService
  ) {}

  ngOnInit(): void {
     SpinnerServiceService.showSpinner();
    this.loadProspects();
    this.loadCurrentUser();
    this.loadTechnicalLeads();
    this.getLoggedInUserAndLoadTags();
    SpinnerServiceService.hideSpinner();

    // Initialize export menu items
    this.initExportMenuItems();

    // Add custom sort function for brands column with default sorting by lastModifiedDate
    this.customSort();
    
    // Register custom filter functions
    this.registerCustomBrandsFilter();
    this.registerCustomOpportunityFilter();
  }
  
  // Get logged in user and load their tags
  getLoggedInUserAndLoadTags() {
    this.userConfigService.getLoggingUser().subscribe(
      (user: LoggingUser) => {
        this.userId = user.id;
        this.loadUserTags();
      },
      error => {
        console.error('Error loading user details:', error);
      }
    );
  }

  // Load user tags from the API
  loadUserTags() {
    if (this.userId === undefined || this.userId === null) {
      console.warn('User ID is not available for loading tags');
      return;
    }
    
    // Continue even if userId is 0 (admin user)
    this.prospectsService.getUserTags(this.userId)
      .subscribe(
        response => {
          this.userTags = response;
          console.log('User tags loaded:', this.userTags);
          
          // Debug tag access
          console.log('Tag access checks:', {
            allProspects: this.hasTagAccess(this.tagCodes.allProspects),
            createOppNeeded: this.hasTagAccess(this.tagCodes.createOppNeeded),
            linkOppNeeded: this.hasTagAccess(this.tagCodes.linkOppNeeded),
            activities: this.hasTagAccess(this.tagCodes.activities)
          });
          
          // Refresh the export menu items after tags are loaded
          this.initExportMenuItems();
          
          // Debug final menu items
          console.log('Export menu items after initialization:', this.exportMenuItems);
        },
        error => {
          console.error('Error loading user tags:', error);
        }
      );
  }

  // Check if user has access to a specific tag
  hasTagAccess(tagCode: string): boolean {
    if (!this.userTags || !this.userTags.user || !this.userTags.user.length) {
      console.warn('User tags not loaded or no tags available:', this.userTags);
      return false;
    }
    
    const hasAccess = this.userTags.user.some((tag: any) => tag.uniqueCode === tagCode);
    console.log(`Checking access for tag ${tagCode}: ${hasAccess}`);
    return hasAccess;
  }
  
  // Initialize the export menu items for the split button
  initExportMenuItems() {
    this.exportMenuItems = [];
    
    // Only add export options if user has the appropriate tag permissions
    if (this.hasTagAccess(this.tagCodes.allProspects)) {
      this.exportMenuItems.push({
        label: 'All Prospects',
        icon: 'pi pi-file-excel',
        command: () => {
          this.exportExcel();
        }
      });
    }
    
    if (this.hasTagAccess(this.tagCodes.linkOppNeeded)) {
      this.exportMenuItems.push({
        label: 'Link Opp Needed',
        icon: 'pi pi-file-excel',
        command: () => {
          this.exportProspectsWithoutOpportunities();
        }
      });
    }
    
    if (this.hasTagAccess(this.tagCodes.createOppNeeded)) {
      this.exportMenuItems.push({
        label: 'Create Opp Needed',
        icon: 'pi pi-file-excel',
        command: () => {
          this.exportCustomersWithoutOpportunities();
        }
      });
    }
    
    if (this.hasTagAccess(this.tagCodes.activities)) {
      this.exportMenuItems.push({
        label: 'Activities',
        icon: 'pi pi-list',
        command: () => {
          this.exportActivities();
        }
      });
    }
    
    // Show warning if no export options are available
    if (this.exportMenuItems.length === 0) {
      console.warn('User has no export permissions');
    }
  }
  
  // Load the current logged-in user
  loadCurrentUser() {
    this.userConfigService.getLoggingUser().subscribe(
      user => {
        this.currentUser = user;
        this.newActivityAttendance = [this.currentUser.id]; // Set current user as default attendee
      },
      error => {
        console.error('Error loading current user:', error);
      }
    );
  }
  
  // Load all technical leads for activity attendance selection
  loadTechnicalLeads() {
    this.salesService.retrieveTechLeads().subscribe(
      response => {
        this.technicalLeadsOptions = response.map(user => ({
          ...user,
          fullName: `${user.firstName} ${user.lastName}`
        }));
      },
      error => {
        console.error('Error loading technical leads:', error);
      }
    );
  }
  
  // Register a custom filter for the brands array
  registerCustomBrandsFilter(): void {
    // Register a custom filter function for array handling
    this.filterService.register('brandsContains', (value: any[], filter: any[]): boolean => {
      if (!filter || filter.length === 0) {
        return true;
      }
      
      if (!value || !Array.isArray(value) || value.length === 0) {
        return false;
      }
      
      // Check if any of the brands in the array match the selected filter values
      return value.some(brand => filter.includes(brand));
    });
  }
  
  // Register a custom filter for opportunities to handle "Not Linked" option
  registerCustomOpportunityFilter(): void {
    this.filterService.register('opportunityFilter', (value: string, filter: string[]): boolean => {
      console.log('opportunityFilter registered function called', { value, filter, valueType: typeof value, valueIsNull: value === null });
      
      // If no filter is selected, show all rows
      if (!filter || filter.length === 0) {
        return true;
      }
      
      // Check if "Not Linked" is selected
      const notLinkedSelected = filter.includes("Not Linked");
      
      // Handle null/undefined values - if "Not Linked" is selected, show these rows
      if (value === null || value === undefined || value === '') {
        return notLinkedSelected;
      }
      
      // For non-null values, check if they're in the filter
      return filter.includes(value);
    });
  }

  // Add custom sorting function for better handling of special columns
  customSort(): void {
    // Using setTimeout to ensure the table is accessible
    setTimeout(() => {
      if (this.table) {
        console.log('Setting up custom sort', {
          tableExists: !!this.table,
          initialSortField: this.table.sortField,
          initialSortOrder: this.table.sortOrder
        });
        
        // Make sure sort mode is single
        this.table.sortMode = 'single';
        
        // Enable custom sorting
        this.table.customSort = true;
        
        // Add a debug method to log sort events
        this.table.onSort.subscribe(event => {
          console.log('Table sort event triggered:', {
            sortField: event.sortField,
            sortOrder: event.sortOrder,
            multiSortMeta: event.multiSortMeta
          });
        });
        
        // Set initial sort field and order
        this.table.sortField = 'lastModifiedDate';
        this.table.sortOrder = -1;
        
        // Add a custom comparator for our specific needs
        this.table._sortField = 'lastModifiedDate'; // Internal property that PrimeNG uses
        
        // Custom sort function for proper date handling
        this.table.sortFunction.subscribe((event) => {
          console.log('Sort function called with:', event);
          const { data, field, order } = event;
          
          if (field === 'lastModifiedDate' || field === 'date' || field === 'creationDate') {
            // Apply date-specific sorting
            return data.sort((data1, data2) => {
              const date1 = data1[field] ? new Date(data1[field]).getTime() : 0;
              const date2 = data2[field] ? new Date(data2[field]).getTime() : 0;
              return this.compareValues(date1, date2, order);
            });
          }
        });
        
        // Force an initial sort after a short delay
        setTimeout(() => {
          console.log('Executing initial sort');
          if (this.table && this.table.sortSingle) {
            this.table.sortSingle();
            
            // Verify the sort was applied
            setTimeout(() => {
              if (this.prospectsData && this.prospectsData.length > 1) {
                const first = this.prospectsData[0];
                const second = this.prospectsData[1];
                console.log('First two rows after sort:', {
                  row1LastModified: first.lastModifiedDate,
                  row2LastModified: second.lastModifiedDate
                });
              }
            }, 200);
          }
        }, 200);
      }
    }, 100);
  }
  
  // Helper method for comparison
  compareValues(value1: any, value2: any, order: number): number {
    if (value1 == null && value2 != null) return order;
    if (value1 != null && value2 == null) return -order;
    if (value1 == null && value2 == null) return 0;
    
    if (typeof value1 === 'string' && typeof value2 === 'string') {
      return value1.localeCompare(value2) * order;
    }
    
    return (value1 < value2 ? -1 : value1 > value2 ? 1 : 0) * order;
  }

  loadProspects(): void {
    this.loading = true;
    this.prospectsService.getAllProspects().subscribe({
      next: (data: Prospect[]) => {
        console.log('Loaded prospects data:', data);
        
        // More detailed debugging for opportunityId
        if (data && data.length > 0) {
          console.log('First few prospects with their opportunityId and opportunityName:');
          data.slice(0, 5).forEach((p, index) => {
            console.log(`Prospect ${index}:`, {
              id: p.id,
              opportunityId: p.opportunityId, 
              opportunityName: p.opportunityName,
              lastModifiedDate: p.lastModifiedDate
            });
          });
        }
        
        // Check if any prospects have opportunityId
        const hasOpportunities = data.some(p => p.opportunityId);
        console.log('At least one prospect has an opportunityId:', hasOpportunities);
        
        // Process values to ensure proper types and formats
        this.prospectsData = data.map(prospect => {
          // Make sure brands is always an array
          const brands = Array.isArray(prospect.brands) ? prospect.brands : [];
          
          // Add a sortableBrand property to help with sorting
          const sortableBrand = brands.length > 0 ? brands[0] : '';
          
          return {
            ...prospect,
            date: prospect.date ? new Date(prospect.date) : null,
            creationDate: prospect.creationDate ? new Date(prospect.creationDate) : null,
            lastModifiedDate: prospect.lastModifiedDate ? new Date(prospect.lastModifiedDate) : null,
            serviceValue: prospect.serviceValue ? parseFloat(prospect.serviceValue.toString()) : 0.0,
            opportunityOrderPrice: prospect.opportunityOrderPrice ? parseFloat(prospect.opportunityOrderPrice.toString()) : null,
            brands: brands.map(brand => brand.endsWith('v1') ? brand.slice(0, -2).trim() : brand.trim()), // Ensure brands are trimmed and cleaned
            // Add this field for sorting
            sortableBrand: sortableBrand
          };
        });
        
        // // Manually sort the data by lastModifiedDate (newest first)
        // this.prospectsData.sort((a, b) => {
        //   const dateA = a.lastModifiedDate ? new Date(a.lastModifiedDate).getTime() : 0;
        //   const dateB = b.lastModifiedDate ? new Date(b.lastModifiedDate).getTime() : 0;
        //   return dateB - dateA; // Descending order (newest first)
        // });
        
        console.log('Data sorted by lastModifiedDate (first 3 items):', 
          this.prospectsData.slice(0, 3).map(p => ({
            id: p.id,
            lastModifiedDate: p.lastModifiedDate
          }))
        );

        this.loading = false;
        this.laodFilters();
        
        // Apply sorting after data is loaded
        setTimeout(() => {
          if (this.table) {
            this.table.sortField = 'lastModifiedDate';
            this.table.sortOrder = -1;
            // No need to call sortSingle() as we manually sorted already
          }
        }, 0);
      },
      error: (error) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load prospects' });
        this.loading = false;


      }
    });



  }

sortTable(event: any) {
  const { field, order } = event;
  this.prospectsData.sort((a, b) => {
    let valueA = a[field];
    let valueB = b[field];

    // Handle dates
    if (field === 'date' || field === 'creationDate' || field === 'lastModifiedDate') {
      valueA = valueA ? new Date(valueA).getTime() : 0;
      valueB = valueB ? new Date(valueB).getTime() : 0;
    }

    // Handle null/undefined
    if (valueA == null && valueB != null) return -order;
    if (valueA != null && valueB == null) return order;
    if (valueA == null && valueB == null) return 0;

    // Handle strings
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return valueA.localeCompare(valueB) * order;
    }

    // Handle numbers
    return (valueA < valueB ? -1 : valueA > valueB ? 1 : 0) * order;
  });
}
  // load Filters
  laodFilters(): void {
    // Customer filter options
    const uniqueCustomers = Array.from(new Set(this.prospectsData.map(p => p.customer))).sort();
    this.optionsCustomer = uniqueCustomers.map(customer => ({
        label: customer,
        value: customer
      }));

    // Created By filter options
    const uniqueCreators = Array.from(new Set(this.prospectsData.map(p => p.prospectCreator))).sort();
    this.optionsCreator = uniqueCreators.map(creator => ({
        label: creator,
        value: creator
      }));

     // Brand filter options - ensure proper data handling
      const uniqueBrands = Array.from(
            new Set(
                this.prospectsData
                    .flatMap(p => p.brands || [])
                    .filter(brand => brand && typeof brand === 'string' && brand.trim())
                    .map(brand => brand.trim())
            )
        ).sort();

        this.optionsBrand = uniqueBrands.map(brand => ({
            label: brand,
            value: brand
        }));

        console.log('Brand filter options:', this.optionsBrand);
        console.log('Prospects brands:', this.prospectsData.map(p => p.brands));

      // console.log("uniqueBrands" , uniqueBrands);
      // console.log("this.optionsBrand" , this.optionsBrand);
      // console.log("this.prospectsData" , this.prospectsData.map(p => p.brands));

    // Sector filter options
      const uniqueSectors = Array.from(
        new Set(this.prospectsData.map(p => p.sector?.trim()).filter(s => s))
      );
      this.optionsSector = uniqueSectors.map(sector => ({
        label: sector,
        value: sector
      }));
       //sales filter options
      const uniqueSales = Array.from(new Set(this.prospectsData.map(p => p.sales))).sort();
      this.optionsSales = uniqueSales.map(sales => ({
        label: sales,
        value: sales
      }));

      // opportunity filter options
      // First, gather all existing opportunity names
      const uniqueOpportunities = Array.from(
        new Set(
          this.prospectsData
            .filter(p => p.opportunityName !== null && p.opportunityName !== undefined && p.opportunityName !== '')
            .map(p => p.opportunityName)
        )
      ).sort();

      // Count how many prospects have no linked opportunity
      const notLinkedCount = this.prospectsData.filter(p => 
        p.opportunityName === null || p.opportunityName === undefined || p.opportunityName === ''
      ).length;

      console.log('Opportunity filter analysis:', {
        totalProspects: this.prospectsData.length,
        prospectsWithOpportunities: this.prospectsData.filter(p => p.opportunityName).length,
        prospectsWithoutOpportunities: notLinkedCount,
        uniqueOpportunities: uniqueOpportunities
      });

      // Create options for the dropdown including the "Not Linked" option
      this.optionsOpportunity = [
        { label: `Not Linked (${notLinkedCount})`, value: "Not Linked" },
        ...uniqueOpportunities.map(opportunity => ({
          label: opportunity,
          value: opportunity
        }))
      ];

  }

  // Add this to debug the filter function
      brandFilterFunction = (value: any, filter: string[], filterLocale?: string): boolean => {
          console.log('brandFilterFunction called', { value, filter });

          // Return true if filter is empty or undefined
          if (!filter || filter.length === 0) {
            console.log('filter is empty or undefined');
            return true;
          }

          // Return false if value is null or undefined
          if (!value) {
            console.log('value is null or undefined');
            return false;
          }

          // Handle case where value is an object (e.g., {label: string, value: string})
          if (value && typeof value === 'object' && 'value' in value) {
            console.log('value is an object with value property', value.value);
            return filter.includes(value.value);
          }

          // Handle case where value is a string
          if (typeof value === 'string') {
            console.log('value is a string', value);
            return filter.includes(value);
          }

          // Handle case where value is an array
          if (Array.isArray(value)) {
            console.log('value is an array', value);
            return value.some(brand => filter.includes(brand));
          }

          // If value is neither a string, array, nor valid object, return false
          console.log('value is neither string, array, nor valid object', value);
          return false;
        };
        
  // Filter function for opportunities to handle "Not Linked" option
  opportunityFilterFunction = (value: any, filter: string[], filterLocale?: string): boolean => {
      console.log('opportunityFilterFunction called', { value, filter, valueType: typeof value, valueIsNull: value === null });
      
      // If no filter is selected, show all rows
      if (!filter || filter.length === 0) {
          console.log('No filter selected, showing all rows');
          return true;
      }
      
      // Check if "Not Linked" is selected
      const notLinkedSelected = filter.includes("Not Linked");
      console.log('Not Linked selected:', notLinkedSelected);
      
      // Handle null/undefined values - if "Not Linked" is selected, show these rows
      if (value === null || value === undefined || value === '') {
          console.log('Value is null/undefined/empty, returning:', notLinkedSelected);
          return notLinkedSelected;
      }
      
      // For non-null values, check if they're in the filter
      const result = filter.includes(value);
      console.log('Value is not null, checking if in filter:', result);
      return result;
  };

  navigateToAddProspect(): void {
    this.router.navigate(['/prospect/add']);
  }

  viewProspect(id: number): void {
    console.log("id" , id);
    this.router.navigate(['/prospect/view', id]
      , { state: { prospectId: id , mode : 'view' } }
    );
  }

  editProspect(id: number): void {
    console.log("id" , id);
    
    // Find the prospect to check if it's a draft
    const prospect = this.prospectsData.find(p => p.id === id);
    const isDraft = prospect?.draftProspect === true;
    
    console.log("Navigating to edit prospect:", {
      id: id,
      isDraft: isDraft,
      prospect: prospect
    });

    this.router.navigate(['/prospect/edit', id]
      , { state: { prospectId: id, mode: 'edit', draftProspect: isDraft } }
    );
  }
  
  onRowClick(event: MouseEvent, prospect: Prospect): void {
    // Check if the click is on a button or a clickable element (to avoid conflicts)
    const target = event.target as HTMLElement;
    const clickedOnButton = target.closest('button, a, .pi, .opportunity-link');
    
    // Only navigate if the click wasn't on a button or other interactive element
    if (!clickedOnButton) {
      console.log('Row clicked, navigating to edit prospect', prospect.id);
      this.editProspect(prospect.id);
    }
  }

  deleteProspect(id: number): void {

    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this prospect?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        SpinnerServiceService.showSpinner();
        this.prospectsService.deleteProspect(id).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Prospect deleted successfully' });
            this.loadProspects();
            SpinnerServiceService.hideSpinner();
          },
          error: () => {
            SpinnerServiceService.hideSpinner();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete prospect' });
          }
        });

      }
    });

  }

  // for  Brands Dialog

  showBrandDialog(id: number): void {
    const prospect = this.prospectsData.find(p => p.id === id);

      if (prospect) {
        this.displayBrands = true;
        this.selectedProspectBrands = prospect.brands;
      } else {
        console.warn(`Prospect with ID ${id} not found.`);
      }
  }
  
  // Open the Add Activity dialog
  openAddActivityDialog(prospect: Prospect) {
    this.selectedProspectForActivity = prospect;
    this.resetActivityForm(); // Reset form fields
    this.displayAddActivity = true; // Show dialog
  }
  
  // Close the Add Activity dialog
  closeAddActivityDialog() {
    this.displayAddActivity = false;
    this.resetActivityForm(); // Clean up form data
  }
  
  // Handle activity attendance change
  onActivityAttendanceChange(event: any) {
    // Event handler for attendance multiselect change
    // No additional action needed as ngModel is already bound to newActivityAttendance
  }
  
  // Handle activity date change
  onActivityDateChange(event: any) {
    // Event handler for calendar date selection
    // No additional action needed as ngModel is already bound to newActivityDate
  }
  
  // Handle file selection
  onActivityFileSelect(event: any) {
    const input = event.target;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const maxSize = 10 * 1024 * 1024; // 10 MB
      if (file.size > maxSize) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'File size exceeds 10 MB limit'
        });
        input.value = '';
        this.newActivityAttachment = null;
        this.newAttachmentBase64 = null;
        return;
      }
      this.newActivityAttachment = file;

      const reader = new FileReader();
      reader.onload = () => {
        const base64String = (reader.result as string).split(',')[1]; // Remove data URL prefix
        this.newAttachmentBase64 = {
          attachmentName: file.name,
          content: base64String,
          contentType: file.type
        };
      };
      reader.onerror = () => {
        console.error('Error reading file');
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to process attachment'
        });
        input.value = '';
        this.newActivityAttachment = null;
        this.newAttachmentBase64 = null;
      };
      reader.readAsDataURL(file);
    } else {
      this.newActivityAttachment = null;
      this.newAttachmentBase64 = null;
    }
  }
  
  // Clear file input
  clearActivityFileInput(): void {
    this.newActivityAttachment = null;
    this.newAttachmentBase64 = null;
    const fileInput = document.getElementById('dialogActivityAttachment') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }
  
  // Helper method to reset activity form fields
  resetActivityForm() {
    this.newActivityTitle = '';
    this.newActivityDescription = '';
    this.newActivityAttachment = null;
    this.newAttachmentBase64 = null;
    this.newActivityDate = null;
    
    // Reset attendance to current user only
    if (this.currentUser && this.currentUser.id) {
      this.newActivityAttendance = [this.currentUser.id];
    } else {
      this.newActivityAttendance = [];
    }
  }
  
  // Add activity to prospect
  addActivityToProspect() {
    if (!this.selectedProspectForActivity) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No prospect selected for adding activity'
      });
      return;
    }
    
    if (this.newActivityTitle?.trim() && this.newActivityDescription?.trim()) {
      // Prepare AppliancesAttachmentDto
      let applianceAttachment: any = null;
      if (this.newAttachmentBase64) {
        applianceAttachment = {
          attachmentName: this.newAttachmentBase64.attachmentName,
          content: this.newAttachmentBase64.content, // Base64 string
          contentType: this.newAttachmentBase64.contentType,
          created: new Date().toISOString(), // ISO string for LocalDateTime
          creatorId: this.currentUser.id || -1 // Use current user ID
        };
      }
      
      // Create activity object
      let activity = {
        prospectId: this.selectedProspectForActivity.id,
        title: this.newActivityTitle,
        description: this.newActivityDescription,
        attendancesIds: this.newActivityAttendance,
        applianceAttachment: applianceAttachment,
        activityDate: this.newActivityDate
      };
      
      SpinnerServiceService.showSpinner();
      
      // Call API to add activity
      this.prospectsService.addActivity(activity).subscribe(
        (response) => {
          console.log('Activity added successfully:', response);
          
          // Show success message
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Activity added successfully'
          });
          
          // Close dialog and reset form
          this.closeAddActivityDialog();
          SpinnerServiceService.hideSpinner();
        },
        (error) => {
          console.error('Error adding activity:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add activity'
          });
          SpinnerServiceService.hideSpinner();
        }
      );
    }
  }


  openNextStateModal(prospect: Prospect): void {
    this.selectedProspect = prospect;
    this.currentState = prospect.status;
    this.nextStateOptions = this.workflowStates[prospect.status] || [];

    // Format the options for the dropdown
    this.formattedNextStateOptions = this.nextStateOptions.map(state => ({
      label: state,
      value: state
    }));

    this.selectedNextState = '';

    // Only open modal if there are next states available
    if (this.nextStateOptions.length > 0) {
      this.displayUpdateModal = true;
    }
  }

  closeModal(): void {
    this.displayUpdateModal = false;
    this.commentText = '';
    this.attachment = null;
    this.selectedProspect = null;
    this.selectedNextState = '';
  }

// for the word limit in title  colunm
  truncateWords(text: string, wordLimit: number): string {
  if (!text) return '';
  const words = text.split(' ');
  return words.length > wordLimit
    ? words.slice(0, wordLimit).join(' ') + '...'
    : text;
}




  onFileSelect(event: any): void {
    // if (event.target.files.length > 0) {
    //   this.attachment = event.target.files[0];
    // }

    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      const maxSizeInBytes = 10 * 1024 * 1024;

      if (file.size > maxSizeInBytes) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'File size exceeds 10 MB limit'
        });
        this.attachment = null;
      } else {
        this.attachment = file;
      }
    }


  }

  async updateProspectStatus(nextState: string): Promise<void> {
    if (!this.selectedProspect || !this.commentText || !nextState) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields' });
      return;
    }

    SpinnerServiceService.showSpinner();
    // In a real app, you might have a proper API call with the form data
    (await this.prospectsService.updateProspectStatus(this.selectedProspect.id, nextState, this.commentText, this.attachment)).subscribe({
      next: () => {
        SpinnerServiceService.hideSpinner();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: `Status updated to ${nextState}` });


        this.prospectsService.getAllProspects().subscribe({
          next: (data: Prospect[]) => {
            this.prospectsData = data;
            this.loading = false;
          },

        });

        this.closeModal();

      },
      error: () => {
        SpinnerServiceService.hideSpinner();
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update status' });
      }
    });
  }

  generateReport(): void {
    this.prospectsService.generateReport().subscribe({
      next: (data: Blob) => {
        const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Prospects_Report.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to generate report' });
      }
    });
  }

  getStatusOptions(): DropdownOption[] {
    const statuses = Object.keys(this.workflowStates);
    return statuses.map(status => ({
      label: status,
      value: status
    }));
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'New':
      case 'Contacted':
        return 'info';
      case 'Engaged':
      case 'Qualified':
      // case 'InProgress':
        return 'warning';
      case 'Proposal Submission':
      case 'Proposal sent':
      case 'Negotiation':
        return 'primary';
      case 'Closed as Won':
        return 'success';
      case 'Closed as Lost':
      case 'De-Qualified':
        return 'danger';
      case 'Re-engage Later':
        return 'secondary';
      default:
        return 'info';
    }
  }

  clear(table: Table) {
    table.clear();
  }

  // sortTable(column: string): void {
  //   // Implementation of table sorting would go here
  //   // This would be handled by PrimeNG table sort mechanisms
  // }

  // exportExcel(): void {
  //   // Implementation of Excel export
  // }

  // exportPdf(): void {
  //   // Implementation of PDF export
  // }



  // exportExcel() {
  //   import("xlsx").then(xlsx => {
  //     let data: any[] = this.table.filteredValue ? this.table.filteredValue : this.table.value;

  //     const worksheet = xlsx.utils.json_to_sheet(data);
  //     const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
  //     const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
  //     this.saveAsExcelFile(excelBuffer, "Prospects");
  //   });
  // }

  // saveAsExcelFile(buffer: any, fileName: string): void {
  //   const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  //   const EXCEL_EXTENSION = '.xlsx';
  //   const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
  //   saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
  // }



//   exportExcel() {
//     import("xlsx").then(xlsx => {
//         let datat: any[] = this.table.filteredValue ? this.table.filteredValue : this.table.value;

//         if (!datat || datat.length === 0) {
//             console.warn("No data available for export.");
//             return;
//         }

//         let index = 0;

//         const data = datat.map((prospect: Prospect) => ({
//             index: ++index,
//             brand: prospect.brand ? prospect.brand.toString() : 'N/A',
//             sector: prospect.sector ? prospect.sector.toString() : 'N/A',
//             customer: prospect.customer ? prospect.customer.toString() : 'N/A',
//             status: prospect.status ? prospect.status.toString() : 'N/A',
//             oddPercent: prospect.oddPercent ? prospect.oddPercent.toString() : 'N/A',
//             title: prospect.title ? prospect.title.toString() : 'N/A',
//             date: prospect.date ? new Date(prospect.date).toISOString().split('T')[0] : 'N/A',
//             serviceValue: prospect.serviceValue ? prospect.serviceValue.toString() : 'N/A',
//             remarks: prospect.remarks ? prospect.remarks.toString() : 'N/A',
//         }));

//         const worksheet = xlsx.utils.json_to_sheet(data);

//         // Adding headers manually to ensure the correct column order
//         const headers = [
//             ['#', 'Brand', 'Sector', 'Customer', 'Status', 'Odd Percent', 'Title', 'Date', 'Service Value', 'Remarks']
//         ];
//         xlsx.utils.sheet_add_aoa(worksheet, headers, { origin: "A1" });

//         const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
//         const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
//         this.saveAsExcelFile(excelBuffer, "Prospects");
//     });
// }

// saveAsExcelFile(buffer: any, fileName: string): void {
//     const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
//     const EXCEL_EXTENSION = '.xlsx';
//     const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
//     saveAs(data, `${fileName}_export_${new Date().getTime()}${EXCEL_EXTENSION}`);
// }

exportExcel() {
    // Check if user has permission to export all prospects
    if (!this.hasTagAccess(this.tagCodes.allProspects)) {
      console.warn("User doesn't have permission to export all prospects");
      return;
    }
    
    import("xlsx").then(xlsx => {
        let datat: any[] = this.table.filteredValue ? this.table.filteredValue : this.table.value;

        if (!datat || datat.length === 0) {
            console.warn("No data available for export.");
            return;
        }

        // Map data to match PDF columns and formatting
        // const data = datat.map((prospect: Prospect, index: number) => ({
        //     index: index + 1,
        //     title: prospect.title ?? 'N/A',
        //     customer: prospect.customer ?? 'N/A',
        //     createdBy: prospect.prospectCreator ?? 'N/A',
        //     createdDate: prospect.creationDate ? new Date(prospect.creationDate).toLocaleDateString() : 'N/A',
        //     brand: prospect.brand ?? 'N/A',
        //     sector: prospect.sector ?? 'N/A',
        //     status: prospect.status ?? 'N/A',
        //     date: prospect.date ? new Date(prospect.date).toLocaleDateString() : 'N/A',
        //    serviceValue: prospect.serviceValue && !isNaN(+prospect.serviceValue)
        //       ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(+prospect.serviceValue)
        //       : 'N/A',

        //     countOfActivities: prospect.countOfActivities ?? 'N/A',
        //     sales: prospect.sales ?? 'N/A'
        // }));


          let totalServiceValue = 0;
            let totalActivities = 0;

          const data = datat.map((prospect: Prospect, index: number) => {
              const rawValue = prospect.serviceValue;
              const rawActivities = prospect.countOfActivities;

              if (!isNaN(rawValue)) totalServiceValue += rawValue;
              if (!isNaN(rawActivities)) totalActivities += rawActivities;

              const rawOpportunityPrice = prospect.opportunityOrderPrice;
              
              return {
                  index: index + 1,
                  title: prospect.title ?? 'N/A',
                  customer: prospect.customer ?? 'N/A',
                  createdBy: prospect.prospectCreator ?? 'N/A',
                  createdDate: prospect.creationDate ? this.datePipe.transform(prospect.creationDate , 'dd/MM/yyyy')  : 'N/A',
                  brand: prospect.brands ?  prospect.brands.join(', ') :  'N/A',
                  sector: prospect.sector ?? 'N/A',
                  status: prospect.status ?? 'N/A',
                  date: prospect.date ? this.datePipe.transform(prospect.date , 'dd/MM/yyyy') : 'N/A',
                  serviceValue: !isNaN(rawValue)
                      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(rawValue)
                      : 'N/A',
                  countOfActivities: !isNaN(rawActivities) ? rawActivities : 'N/A',
                  sales: prospect.sales ?? 'N/A',
                  opportunity: prospect.opportunityName ? prospect.opportunityName : 'Not Linked',
                  opportunitySSS: !isNaN(rawOpportunityPrice)
                      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(rawOpportunityPrice)
                      : '-'
              };
          });

          // Calculate the total for opportunityOrderPrice
          let totalSSS = 0;
          datat.forEach((prospect: Prospect) => {
              const rawSSS = prospect.opportunityOrderPrice;
              if (!isNaN(rawSSS)) totalSSS += rawSSS;
          });

          // Add the total row
          data.push({
              index: null,  // leave index empty
              title: 'TOTAL',
              customer: '',
              createdBy: '',
              createdDate: '',
              brand: '',
              sector: '',
              status: '',
              date: '',
              serviceValue: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalServiceValue),
              countOfActivities: totalActivities,
              sales: '',
              opportunity: '',
              opportunitySSS: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalSSS)
          });





        // Define headers to match PDF columns
        const headers = [
            ['#', 'Title', 'Customer', 'Created By', 'Created Date', 'Brand', 'Sector', 'Status', 'Date', 'Service Value', 'Activities Count', 'Sales', 'Opportunity', 'SSS']
        ];

        // Create worksheet with headers and data
        const worksheet = xlsx.utils.json_to_sheet([], { skipHeader: true });

        // Add headers manually with styling
        const headerStyle = {
            font: { bold: true, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '2980B9' } },
            alignment: { horizontal: 'center' },
            border: {
                top: { style: 'thin', color: { rgb: '000000' } },
                bottom: { style: 'thin', color: { rgb: '000000' } },
                left: { style: 'thin', color: { rgb: '000000' } },
                right: { style: 'thin', color: { rgb: '000000' } }
            }
        };

        // Add headers to the first row
        headers[0].forEach((header, index) => {
            const cellAddress = xlsx.utils.encode_cell({ r: 0, c: index });
            worksheet[cellAddress] = {
                v: header,
                t: 's',
                s: headerStyle
            };
        });

        // Add data starting from row 2
        xlsx.utils.sheet_add_json(worksheet, data, { origin: 'A2', skipHeader: true });

        // Column indices to center (matching PDF's columnStyles)
        const columnKeys = ['index', 'title', 'customer', 'createdBy', 'createdDate', 'brand', 'sector', 'status', 'date', 'serviceValue', 'countOfActivities', 'sales', 'opportunity', 'opportunitySSS'];
        const centeredColumns = ['index', 'brand', 'sector', 'customer', 'status', 'title', 'date', 'serviceValue', 'opportunitySSS'];
        const centeredColumnIndices = centeredColumns.map(key => columnKeys.indexOf(key));

        // Calculate range dynamically
        const range = xlsx.utils.decode_range(`A1:${xlsx.utils.encode_col(columnKeys.length - 1)}${data.length + 1}`);

        // Apply centering to specific columns for data rows
        for (let C = range.s.c; C <= range.e.c; ++C) {
            if (centeredColumnIndices.includes(C)) {
                for (let R = 1; R <= range.e.r; ++R) {
                    const cell = xlsx.utils.encode_cell({ r: R, c: C });
                    if (!worksheet[cell]) {
                        worksheet[cell] = { v: '', t: 's' };
                    }
                    worksheet[cell].s = {
                        alignment: { horizontal: 'center' },
                        border: {
                            top: { style: 'thin', color: { rgb: '000000' } },
                            bottom: { style: 'thin', color: { rgb: '000000' } },
                            left: { style: 'thin', color: { rgb: '000000' } },
                            right: { style: 'thin', color: { rgb: '000000' } }
                        }
                    };
                }
            }
        }

        // Set column widths
        worksheet['!cols'] = [
            { wch: 5 },  // index
            { wch: 25 }, // title
            { wch: 25 }, // customer
            { wch: 25 }, // createdBy
            { wch: 25 }, // createdDate
            { wch: 25 }, // brand
            { wch: 25 }, // sector
            { wch: 25 }, // status
            { wch: 25 }, // date
            { wch: 25 }, // serviceValue
            { wch: 25 }, // countOfActivities
            { wch: 25 }  // sales
        ];

        const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
        const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
        this.saveAsExcelFile(excelBuffer, 'Prospects');
    });
}

saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    saveAs(data, `${fileName}_export_${new Date().getTime()}${EXCEL_EXTENSION}`);
}






// PDF Export
exportPdf() {
  const doc = new jsPDF('landscape', 'pt', 'a4');

  const columns = [
      { header: '#', dataKey: 'index' },
      { header: 'Title', dataKey: 'title' },
      { header: 'Customer', dataKey: 'customer' },
      { header: 'Created By', dataKey: 'createdBy' },
      { header: 'Created Date', dataKey: 'createdDate' },
      { header: 'Brand', dataKey: 'brand' },
      { header: 'Sector', dataKey: 'sector' },
      { header: 'Status', dataKey: 'status' },
      { header: 'Date', dataKey: 'date' },
      { header: 'Service Value', dataKey: 'serviceValue' },
      { header: 'Activities Count', dataKey: 'countOfActivities' },
      { header: 'Sales', dataKey: 'sales' },
      { header: 'Opportunity', dataKey: 'opportunity' },
      { header: 'SSS', dataKey: 'opportunitySSS' }
  ];
const datat: any[] = this.table.filteredValue ? this.table.filteredValue : this.table.value;

  if (!datat || datat.length === 0) {
    console.warn("No data available for export.");
    return;
  }

  let totalServiceValue = 0;
  let totalActivities = 0;

  // Map data with the same logic as exportExcel
  const data = datat.map((prospect: Prospect, index: number) => {
    const rawValue = prospect.serviceValue;
    const rawActivities = prospect.countOfActivities;

    if (!isNaN(rawValue)) totalServiceValue += rawValue;
    if (!isNaN(rawActivities)) totalActivities += rawActivities;

    return {
      index: index + 1,
      title: prospect.title ?? 'N/A',
      customer: prospect.customer ?? 'N/A',
      createdBy: prospect.prospectCreator ?? 'N/A',
      createdDate: prospect.creationDate ? this.datePipe.transform(prospect.creationDate, 'dd/MM/yyyy') : 'N/A',
      brand: prospect.brands ?? 'N/A',
      sector: prospect.sector ?? 'N/A',
      status: prospect.status ?? 'N/A',
      date: prospect.date ? this.datePipe.transform(prospect.date, 'dd/MM/yyyy') : 'N/A',
      serviceValue: !isNaN(rawValue)
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(rawValue)
        : 'N/A',
      countOfActivities: !isNaN(rawActivities) ? rawActivities : 'N/A',
      sales: prospect.sales ?? 'N/A',
      opportunity: prospect.opportunityName ? prospect.opportunityName : 'Not Linked',
      opportunitySSS: prospect.opportunityOrderPrice ? 
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(prospect.opportunityOrderPrice) : '-'
    };
  });

  // Calculate the total for opportunityOrderPrice (SSS)
  let totalSSS = 0;
  datat.forEach((prospect: Prospect) => {
    const rawSSS = prospect.opportunityOrderPrice;
    if (!isNaN(rawSSS)) totalSSS += rawSSS;
  });

  // Add the total row
  data.push({
    index: null as any, // leave index empty
    title: 'TOTAL',
    customer: '',
    createdBy: '',
    createdDate: '',
    brand: '',
    sector: '',
    status: '',
    date: '',
    serviceValue: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalServiceValue),
    countOfActivities: totalActivities,
    sales: '',
    opportunity: '',
    opportunitySSS: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalSSS)
  });

  autoTable(doc, {
      columns: columns,
      body: data,
      theme: 'grid',
      styles: {
          font: 'helvetica',
          fontSize: 7,
          cellPadding: 2,
          overflow: 'linebreak',
          cellWidth: 'wrap'
      },
      headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
      },
      alternateRowStyles: {
          fillColor: [240, 240, 240]
      },
      columnStyles: {
          index: { halign: 'center' },
          brand: { halign: 'center' },
          sector: { halign: 'center' },
          customer: { cellWidth: "auto", minCellWidth: 70,cellPadding: 4, halign: 'center' },
          status: { halign: 'center' },
          oddPercent: { halign: 'center' },
          title: { cellWidth: "auto", minCellWidth: 70,cellPadding: 4, halign: 'center' },
          date: { halign: 'center' },
          serviceValue: { halign: 'center' },
          opportunitySSS: { halign: 'center' },  // Added styling for SSS column
          countOfActivities: { halign: 'center' },
          sales: { halign: 'center' },
          createdBy: { halign: 'center' },
          createdDate: { halign: 'center' },
          remarks: { halign: 'center' },
          opportunity: { cellWidth: "auto", minCellWidth: 70, cellPadding: 4, halign: 'center' }
      },
      margin: { top: 20 } ,
      didParseCell: (data) => {
      // Style the total row (optional, for visual distinction)
      if (data.row.index === data.table.body.length - 1 && data.row.section === 'body') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [220, 220, 220]; // Light gray background for total row
      }
    }
  });


  doc.save('Prospects.pdf');
}




  navigateToOpportunityDetails(opportunityId: number): void {
    if (opportunityId) {
      console.log('Navigating to opportunity details with ID:', opportunityId);
      let filter = { id: opportunityId };
      this.router.navigate(['/leads/'], { queryParams: filter, skipLocationChange: true });
      history.pushState(filter, "", "/leads/");
    } else {
      console.warn('Cannot navigate: opportunityId is null or undefined');
    }
  }

  // For debugging click events and initializing table sort
  ngAfterViewInit() {
    // Initialize sorting by lastModifiedDate
    setTimeout(() => {
      if (this.table) {
        console.log('Setting initial sort in ngAfterViewInit');
        this.table.sortField = 'lastModifiedDate';
        this.table.sortOrder = -1;
        this.table.sortSingle();
      }
      
      // Check for opportunity links
      const oppLinks = document.querySelectorAll('.opportunity-link');
      console.log('Found opportunity links:', oppLinks.length);
      
      if (oppLinks.length === 0) {
        console.warn('No opportunity links found in DOM. Check HTML structure and CSS selectors.');
      }
      
      // Add direct event listeners to ensure clicks are captured
      oppLinks.forEach((link, i) => {
        console.log(`Link ${i} opportunity ID:`, link.getAttribute('data-opportunity-id'));
        
        link.addEventListener('click', (e) => {
          console.log(`DEBUG: Link ${i} clicked via direct DOM event!`, e);
          const opportunityId = link.getAttribute('data-opportunity-id');
          if (opportunityId) {
            console.log(`Direct navigation to opportunity ID: ${opportunityId}`);
            // Uncomment to try direct navigation if Angular binding fails
            // this.navigateToOpportunityDetails(parseInt(opportunityId, 10));
          }
        });
      });
      
      // Check if we're in a test environment or if Angular is working properly
      console.log('Angular change detection running:', !!this.table);
    }, 1000); // Wait for view to be fully rendered
  }

  exportActivities() {
    // Check if user has permission to export Activities
    if (!this.hasTagAccess(this.tagCodes.activities)) {
      console.warn("User doesn't have permission to export Activities");
      return;
    }
    
    // Get filtered prospects or all prospects if no filter is applied
    const filteredProspects: any[] = this.table.filteredValue ? this.table.filteredValue : this.table.value;
    
    // Check if there are prospects
    if (!filteredProspects || filteredProspects.length === 0) {
        this.messageService.add({
            severity: 'warn', 
            summary: 'No Data', 
            detail: 'No prospects available for export'
        });
        return;
    }
    
    // Collect all activity IDs from filtered prospects
    const activityIds: number[] = [];
    
    filteredProspects.forEach(prospect => {
        if (prospect.prospectActivityIds && prospect.prospectActivityIds.length > 0) {
            // Add all activity IDs from this prospect to our collection
            activityIds.push(...prospect.prospectActivityIds);
        }
    });
    
    if (activityIds.length === 0) {
        const isFiltered = this.table.filteredValue && this.table.filteredValue.length > 0;
        this.messageService.add({
            severity: 'warn', 
            summary: 'No Activities', 
            detail: isFiltered ? 
                'No activities found for the filtered prospects' : 
                'No activities found for export'
        });
        return;
    }
    
    // Show spinner while fetching activities
    SpinnerServiceService.showSpinner();
    
    // Use our new service method to get activities by IDs
    this.prospectsService.getProspectActivitiesByIds(activityIds).subscribe({
        next: (activities: any[]) => {
            // Process the activities once received
            this.processAndExportActivities(activities, filteredProspects);
            SpinnerServiceService.hideSpinner();
        },
        error: (error) => {
            console.error('Error fetching prospect activities:', error);
            this.messageService.add({
                severity: 'error', 
                summary: 'Error', 
                detail: 'Failed to fetch prospect activities for export'
            });
            SpinnerServiceService.hideSpinner();
        }
    });
}

/**
 * Process retrieved activities and export them to Excel
 * @param activities Activities data from API
 * @param prospects Prospect data for mapping activity to prospect
 */
private processAndExportActivities(activities: any[], prospects: any[]) {
    import("xlsx").then(xlsx => {
        if (!activities || activities.length === 0) {
            this.messageService.add({
                severity: 'warn', 
                summary: 'No Activities', 
                detail: 'No activities found for export'
            });
            return;
        }
        
        // Create a map of activity IDs to their prospects
        const activityToProspectMap = new Map();
        
        // Go through each prospect and map its activities to it
        prospects.forEach(prospect => {
            // If the prospect has activity IDs
            if (prospect.prospectActivityIds && prospect.prospectActivityIds.length > 0) {
                // Map each activity ID to this prospect's details
                prospect.prospectActivityIds.forEach((activityId: number) => {
                    activityToProspectMap.set(activityId, {
                        title: prospect.title || 'N/A',
                        creator: prospect.prospectCreator || 'N/A'
                    });
                });
            }
        });
        
        // Format activities for export
        const formattedActivities: any[] = [];
        let index = 0;
        
        activities.forEach(activity => {
            // Format attendance as comma-separated list of names
            const attendanceNames = activity.attendances?.map(user => 
                `${user.firstName} ${user.lastName}`
            ).join(', ') || '';
            
            // Find the prospect this activity belongs to using our mapping
            const prospectInfo = activityToProspectMap.get(activity.id) || { title: 'N/A', creator: 'N/A' };
            
            formattedActivities.push({
                index: ++index,
                title: activity.title || 'N/A',
                creator: activity.createdBy ? 
                        `${activity.createdBy.firstName} ${activity.createdBy.lastName}` : 'N/A',
                attendance: attendanceNames,
                description: activity.description || 'N/A',
                date: `${activity.postedOnDate || ''} ${activity.postedOnTime || ''}`,
                prospectTitle: prospectInfo.title,
                prospectCreator: prospectInfo.creator
            });
        });
        
        // Create worksheet
        const worksheet = xlsx.utils.json_to_sheet(formattedActivities);
        
        // Add headers
        const headers = [
            ['#', 'Title', 'Creator', 'Attendance', 'Description', 'Date', 'Prospect Title', 'Prospect Creator']
        ];
        xlsx.utils.sheet_add_aoa(worksheet, headers, { origin: "A1" });
        
        // Create workbook and export
        const workbook = { Sheets: { 'activities': worksheet }, SheetNames: ['activities'] };
        const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
        
        // Add 'Filtered' to the filename if we're exporting filtered data
        const isFiltered = this.table.filteredValue && this.prospectsData.length !== prospects.length;
        const fileName = isFiltered ? "Filtered_Prospect_Activities" : "Prospect_Activities";
        this.saveAsExcelFile(excelBuffer, fileName);
        
        this.messageService.add({
            severity: 'success', 
            summary: 'Export Successful', 
            detail: `${formattedActivities.length} activities exported`
        });
    });
}

/**
 * Export prospects without opportunities where the customer has other opportunities
 * Downloads data as Excel file
 */
exportProspectsWithoutOpportunities() {
  // Check if user has permission to export Link Opp Needed
  if (!this.hasTagAccess(this.tagCodes.linkOppNeeded)) {
    console.warn("User doesn't have permission to export Link Opp Needed");
    return;
  }
  
  this.loading = true;
  this.messageService.add({
    severity: 'info',
    summary: 'Export Starting',
    detail: 'Fetching prospects without opportunities...'
  });
  
  this.prospectsService.getProspectsWithoutOpportunitiesWithCustomerHavingOpportunities()
    .subscribe({
      next: (data: ProspectCustomerDetailsDTO[]) => {
        this.loading = false;
        if (data && data.length > 0) {
          import("xlsx").then(xlsx => {
            // Prepare the data for Excel
            const exportData = data.map((item, index) => ({
              '#': index + 1,
              'Prospect ID': item.prospectId,
              'Prospect Name': item.prospectName,
              'Customer Name': item.customerName,
              'Technical Leads': item.technicalLeads
            }));
            
            // Create worksheet
            const worksheet = xlsx.utils.json_to_sheet(exportData);
            
            // Add headers
            const headers = [
              ['#', 'Prospect ID', 'Prospect Name', 'Customer Name', 'Technical Leads']
            ];
            xlsx.utils.sheet_add_aoa(worksheet, headers, { origin: "A1" });
            
            // Create workbook and export
            const workbook = { Sheets: { 'Prospects Without Opportunities': worksheet }, SheetNames: ['Prospects Without Opportunities'] };
            const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
            
            // Generate filename with date
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD format
            const fileName = `Prospects_Without_Opportunities_${dateStr}`;
            this.saveAsExcelFile(excelBuffer, fileName);
            
            this.messageService.add({
              severity: 'success',
              summary: 'Export Successful',
              detail: `${data.length} prospects without opportunities exported`
            });
          });
        } else {
          this.messageService.add({
            severity: 'info',
            summary: 'No Data',
            detail: 'No prospects without opportunities found'
          });
        }
      },
      error: (error) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Export Failed',
          detail: 'Failed to export prospects: ' + error
        });
      }
    });
}

/**
 * Export prospects with customers without opportunities
 * Downloads data as Excel file
 */
exportCustomersWithoutOpportunities() {
  // Check if user has permission to export Create Opp Needed
  if (!this.hasTagAccess(this.tagCodes.createOppNeeded)) {
    console.warn("User doesn't have permission to export Create Opp Needed");
    return;
  }
  
  this.loading = true;
  this.messageService.add({
    severity: 'info',
    summary: 'Export Starting',
    detail: 'Fetching customers without opportunities...'
  });
  
  this.prospectsService.getProspectsWithCustomersWithoutOpportunities()
    .subscribe({
      next: (data: CustomerProspectDTO[]) => {
        this.loading = false;
        if (data && data.length > 0) {
          import("xlsx").then(xlsx => {
            // Prepare the data for Excel
            const exportData = data.map((item, index) => ({
              '#': index + 1,
              'Prospect ID': item.prospectId,
              'Prospect Name': item.prospectName,
              'Customer Name': item.customerName
            }));
            
            // Create worksheet
            const worksheet = xlsx.utils.json_to_sheet(exportData);
            
            // Add headers
            const headers = [
              ['#', 'Prospect ID', 'Prospect Name', 'Customer Name']
            ];
            xlsx.utils.sheet_add_aoa(worksheet, headers, { origin: "A1" });
            
            // Create workbook and export
            const workbook = { Sheets: { 'Customers Without Opportunities': worksheet }, SheetNames: ['Customers Without Opportunities'] };
            const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
            
            // Generate filename with date
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD format
            const fileName = `Customers_Without_Opportunities_${dateStr}`;
            this.saveAsExcelFile(excelBuffer, fileName);
            
            this.messageService.add({
              severity: 'success',
              summary: 'Export Successful',
              detail: `${data.length} customers without opportunities exported`
            });
          });
        } else {
          this.messageService.add({
            severity: 'info',
            summary: 'No Data',
            detail: 'No customers without opportunities found'
          });
        }
      },
      error: (error) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Export Failed',
          detail: 'Failed to export customers: ' + error
        });
      }
    });
}



}